import { useTranslation } from 'next-i18next';
import { format } from 'date-fns';
import {
  Flex,
  Box,
  Heading,
  Tag,
  Text,
  VStack,
  HStack,
  Container,
  GridItem,
  Grid,
} from '@chakra-ui/react';
import { useRouter } from 'next/router';
import formatCurrency from '@/lib/formatCurrency';
import { listingTags } from '@/lib/listingUtils';
import { ListingProps } from '@/types/listing';
import formatHectares from '@/lib/formatHectares';
import { getLocalizedRegionName } from '@/lib/regions';
import formatNumber from '@/lib/formatNumber';

const ListingInfoCardHorizontal = ({ listing, forestData }: ListingProps) => {
  const { t } = useTranslation('backoffice');
  const tags = listingTags(listing, t);
  const { locale } = useRouter();
  // Function that checks if the listing contains any manually inputted data

  return (
    <Container maxW="container.2xl" py={3}>
      <Grid templateColumns="repeat(7, 1fr)" gap={6} rowGap="1" py={5}>
        <GridItem colSpan={[7, 7, 2]}>
          <Box background="#F9F8F6">
            {tags.map((tag) => (
              <Tag
                textTransform="uppercase"
                size="sm"
                mr="2"
                colorScheme="success"
                py={1}
                key={tag}
              >
                {tag}
              </Tag>
            ))}
            <Heading as="h1" size="lg" fontWeight="600" mb={2}>
              {listing.name}
            </Heading>
            <Text mb={1}>
              {Array.from(
                new Set(
                  listing.properties.map((property) =>
                    getLocalizedRegionName(
                      property.property_municipality,
                      locale,
                    ),
                  ),
                ),
              ).join(', ')}
            </Text>

            <Text
              textTransform="uppercase"
              fontSize="xs"
              fontWeight="400"
              color="gray.500"
              mb={8}
            >
              {t('listing_number', 'Kohdenumero')}: {listing.public_id}
            </Text>
          </Box>
        </GridItem>
        <GridItem colSpan={[7, 7, 4, 3]}>
          <HStack alignItems="flex-start">
            <VStack
              spacing={5}
              alignItems="flex-start"
              width={['100%', '100%', '50%']}
            >
              <Flex flexDir="column">
                <Text fontSize="sm">{t('area')}</Text>
                <Text fontSize="2xl" fontWeight="600" color="primary.500">
                  {formatHectares(listing.total_area_in_hectares as number, {
                    approximate: listing.has_approximate_area,
                  })}
                </Text>
              </Flex>
              <Flex flexDir="column">
                <Text fontSize="sm">
                  {t('forest_amount_usable', 'Ainespuun määrä')}
                </Text>
                <Text fontSize="2xl" fontWeight="600" color="primary.500">
                  {formatNumber(forestData?.combined?.totalVolume, {
                    fallbackValue: '-',
                    unit: 'm³',
                  })}
                </Text>
              </Flex>
            </VStack>
            <VStack
              spacing={5}
              alignItems="flex-start"
              width={['100%', '100%', '50%']}
            >
              {listing.publish_end_datetime && (
                <Flex flexDir="column">
                  <Text fontSize="sm">
                    {t('offer_latest', 'Tarjoa viimeistään')}
                  </Text>
                  <Text fontSize="2xl" fontWeight="600" color="primary.500">
                    {format(
                      new Date(listing.publish_end_datetime as string),
                      'd.M.yyyy HH:mm',
                    )}
                  </Text>
                </Flex>
              )}

              <Flex flexDir="column">
                <Text fontSize="sm">
                  {listing.sales_assignment?.price_type === 'request'
                    ? t('price_type_request', 'Hintapyyntö')
                    : t('price_type_minimum', 'Lähtöhinta')}
                </Text>
                <Text fontSize="2xl" fontWeight="600" color="primary.500">
                  {formatCurrency(listing?.total_price as number, {
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 2,
                  })}
                </Text>
              </Flex>
            </VStack>
          </HStack>
        </GridItem>
      </Grid>
    </Container>
  );
};

export default ListingInfoCardHorizontal;
